import { useCallback, useMemo, useRef } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { likePetitionUpdate } from '#src/app/shared/api/petitionUpdate';
import { useCurrentUserAsync, useLoginStateAsync } from '#src/app/shared/hooks/session';
import { isLoaded } from '#src/app/shared/utils/async';

import type { PetitionUpdateType } from './types';
import { useLikePetitionUpdateState } from './useLikePetitionUpdateState';
import { useLikePetitionUpdateTracking } from './useLikePetitionUpdateTracking';

type PetitionUpdateLikeData = {
	id: string;
	likes: number | null;
	likesLocalized: string | null;
	viewerLiked: boolean;
};

type Result = ModelHookResult<
	{
		isLiked: boolean;
		likeCount: number;
		likeCountLocalized: string;
		isGuest: boolean;
	},
	{
		toggleLike: () => Promise<undefined>;
	}
>;

export function useLikePetitionUpdate({
	petitionUpdate,
	petitionId,
	updateType,
	trackingData,
}: {
	petitionUpdate: PetitionUpdateLikeData;
	petitionId: string;
	updateType: PetitionUpdateType;
	// Extra dimensions merged into every like event (e.g. the page/section the like happened on).
	trackingData?: Readonly<Record<string, string | number | boolean | undefined>>;
}): Result {
	const { id } = petitionUpdate;
	const loginStateBase = useLoginStateAsync();
	const loginState = isLoaded(loginStateBase) ? loginStateBase.value : undefined;

	const currentUserBase = useCurrentUserAsync();
	const currentUser = isLoaded(currentUserBase) && currentUserBase.value;

	const utilityContext = useUtilityContext();

	const {
		data: { isLiked, likeCount, likeCountLocalized },
		actions: { updateLike },
	} = useLikePetitionUpdateState({ petitionUpdate });

	const petitionUpdateLikeTrackingData = useMemo(() => {
		return {
			// Spread first so the canonical fields below always win over caller-supplied dimensions.
			...trackingData,
			petition_id: petitionId,
			petition_update_id: id,
			update_type: updateType,
			liker_id: currentUser ? currentUser.id : undefined,
		};
	}, [petitionId, id, updateType, currentUser, trackingData]);

	const {
		actions: { trackLikePetitionUpdate, trackError },
	} = useLikePetitionUpdateTracking({ petitionUpdateLikeTrackingData });

	const inFlightRef = useRef(false);

	const toggleLike = useCallback(async (): Promise<undefined> => {
		// Ignore rapid re-taps while a toggle is in flight: overlapping mutations can let an
		// out-of-order server response desync the UI from the server (CHANGE-94590 review).
		if (inFlightRef.current) return;
		inFlightRef.current = true;

		trackLikePetitionUpdate('petition_update_like_click', {
			likeState: !isLiked,
		});
		updateLike(!isLiked);

		try {
			const likeResult = await likePetitionUpdate({ petitionUpdateId: id, like: !isLiked, utilityContext });
			if (likeResult?.likePetitionUpdate.success) {
				trackLikePetitionUpdate('petition_update_like_success', {
					likeState: !!likeResult.likePetitionUpdate.liked,
				});
			} else {
				updateLike(isLiked);
				trackLikePetitionUpdate('petition_update_like_failure', {
					likeState: !!likeResult?.likePetitionUpdate.liked,
					errors: likeResult?.likePetitionUpdate.reason || 'an unknown reason',
				});
			}
		} catch (err) {
			updateLike(isLiked);
			trackError('petition_update_like_error', err, {
				params: petitionUpdateLikeTrackingData,
			});
		} finally {
			inFlightRef.current = false;
		}
	}, [id, isLiked, updateLike, trackLikePetitionUpdate, utilityContext, trackError, petitionUpdateLikeTrackingData]);

	return {
		data: {
			isGuest: loginState === 'GUEST',
			isLiked,
			likeCount,
			likeCountLocalized,
		},
		actions: {
			toggleLike,
		},
	};
}
